<mat-sidenav-container class="report-builder-container">

  <!-- Left Actions Sidenav -->
  <mat-sidenav #actionsSidenav mode="side" opened position="start" class="actions-sidenav">
    <div class="sidenav-section">
      <h4 class="sidenav-heading">Report Builder</h4>
      <div class="sidenav-actions">
        <button mat-button color="primary" (click)="goBackToReports()">
          <mat-icon>arrow_back</mat-icon> All Reports
        </button>
      </div>
    </div>
    <mat-divider></mat-divider>

    <div class="sidenav-section">
      <h4 class="sidenav-heading">Actions</h4>
      <div class="sidenav-actions">
        <button mat-button color="primary" (click)="saveReport()" [disabled]="!reportName()">
          <mat-icon>save</mat-icon> Save Report
        </button>
        <button mat-button color="primary" (click)="sendReportNow()">
          <mat-icon>send</mat-icon> Send Now
        </button>
        <button mat-button color="primary" (click)="configureSchedule()">
          <mat-icon>schedule</mat-icon> Schedule
        </button>
        <button mat-button color="primary" [matMenuTriggerFor]="exportMenu">
          <mat-icon>download</mat-icon> Export
        </button>
        <mat-menu #exportMenu="matMenu">
          <button mat-menu-item (click)="exportCSV()">
            <mat-icon>table_chart</mat-icon> Export as CSV
          </button>
          <button mat-menu-item (click)="exportExcel()">
            <mat-icon>grid_on</mat-icon> Export as Excel
          </button>
        </mat-menu>
      </div>
    </div>
    <mat-divider></mat-divider>

    <div class="sidenav-section">
      <h4 class="sidenav-heading">Tips</h4>
      <p class="sidenav-tip">Use the <strong>Columns</strong> panel on the right to choose which fields to include.</p>
      <p class="sidenav-tip">Use the <strong>Filters</strong> panel to narrow down your results.</p>
      <p class="sidenav-tip">Click column headers to sort.</p>
    </div>
  </mat-sidenav>

  <!-- Main Content -->
  <mat-sidenav-content class="builder-content">

    <!-- Top Header Bar -->
    <div class="builder-header">
      <div class="header-title-area">
        <!-- Editable Report Title -->
        <div class="report-title-wrapper" (click)="focusNameInput()">
          @if (!isEditingName) {
            <h1 class="report-title-display" [class.unnamed]="!reportName()">
              <mat-icon class="title-edit-icon">edit</mat-icon>
              {{ reportName() || 'Click to name this report' }}
            </h1>
          }
          @if (isEditingName) {
            <input #nameInput
                   class="report-title-input"
                   [ngModel]="reportName()"
                   (ngModelChange)="onReportNameChange($event)"
                   (blur)="isEditingName = false"
                   (keyup.enter)="nameInput.blur()"
                   placeholder="Type a report name..." />
          }
        </div>

        <!-- Entity selector sits below the title -->
        <div class="header-meta-row">
          <mat-form-field appearance="outline" class="entity-type-field">
            <mat-label>Data Source</mat-label>
            <mat-select [ngModel]="entityType()" (ngModelChange)="onEntityTypeChange($event)">
              @for (entity of entityList; track entity.key) {
                <mat-option [value]="entity.key">
                  <mat-icon>{{ entity.icon }}</mat-icon>
                  {{ entity.label }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>

          <!-- Debug: Active Filter Description -->
          @if (activeFilterCount() > 0) {
            <button mat-stroked-button class="debug-filter-btn" (click)="showFilterDebug = !showFilterDebug">
              <mat-icon>bug_report</mat-icon> {{ activeFilterCount() }} filter{{ activeFilterCount() === 1 ? '' : 's' }}
            </button>
          }

          <div class="header-actions">
            @if (currentSchedule()?.enabled) {
              <span class="schedule-indicator" (click)="configureSchedule()">
                <mat-icon>schedule</mat-icon> Scheduled
              </span>
            }
            @if (isDirty()) {
              <span class="unsaved-indicator">
                <mat-icon>edit</mat-icon> Unsaved changes
              </span>
            }
            <button mat-raised-button color="primary" (click)="saveReport()" [disabled]="!reportName()">
              <mat-icon>save</mat-icon> Save
            </button>
          </div>
        </div>
      </div>

      @if (showFilterDebug) {
        <div class="debug-filter-panel">
          <div class="debug-header">
            <strong>Active Filters</strong>
            <button mat-icon-button (click)="copyFilterDebug()" matTooltip="Copy to clipboard">
              <mat-icon>content_copy</mat-icon>
            </button>
          </div>
          <pre class="debug-description">{{ getFilterDescription() }}</pre>
          <details class="debug-raw">
            <summary>Raw filter model (JSON)</summary>
            <pre>{{ getFilterModelJson() }}</pre>
          </details>
        </div>
      }
    </div>

    <!-- Tabs: AI Assistant & Manual Builder -->
    <mat-tab-group [(selectedIndex)]="selectedTabIndex" class="builder-tabs">

      <!-- Tab 1: AI Assistant -->
      <mat-tab>
        <ng-template mat-tab-label>
          <mat-icon>auto_awesome</mat-icon>&nbsp;AI Assistant
        </ng-template>

        <div class="ai-assistant-container">
          <div class="ai-messages" #aiMessagesContainer>
            <!-- Welcome message (shown when no conversation) -->
            @if (aiMessages.length === 0) {
              <div class="ai-message assistant-message">
                <mat-icon class="ai-avatar">auto_awesome</mat-icon>
                <div class="message-bubble assistant-bubble">
                  <p><strong>AI Report Assistant</strong></p>
                  <p>Describe the report you'd like to build in plain language. For example:</p>
                  <ul>
                    <li>"Show me all contracts expiring in the next 90 days"</li>
                    <li>"List all parties in California with active contracts"</li>
                    <li>"Find provisions with high risk scores"</li>
                    <li>"Add the department column and sort by value"</li>
                  </ul>
                  <p class="ai-hint">I can also modify reports — just tell me what to change.</p>
                </div>
              </div>
            }

            <!-- Conversation messages -->
            @for (msg of aiMessages; track $index) {
              @if (msg.role === 'user') {
                <div class="ai-message user-message">
                  <div class="message-bubble user-bubble">
                    {{ msg.content }}
                  </div>
                  <mat-icon class="user-avatar">person</mat-icon>
                </div>
              }

              @if (msg.role === 'assistant') {
                <div class="ai-message assistant-message">
                  <mat-icon class="ai-avatar">auto_awesome</mat-icon>
                  <div class="message-bubble assistant-bubble">
                    <p>{{ msg.content }}</p>
                    @if (msg.gridState) {
                      <div class="ai-report-applied">
                        <mat-icon>check_circle</mat-icon>
                        <span>Report applied to grid</span>
                        <button mat-button color="primary" (click)="selectedTabIndex = 1" class="view-results-btn">
                          View Results
                        </button>
                      </div>
                    }
                  </div>
                </div>
              }

              @if (msg.role === 'system') {
                <div class="ai-message system-message">
                  <div class="message-bubble system-bubble">
                    <mat-icon>info</mat-icon>
                    <span>{{ msg.content }}</span>
                    @if (msg.content.includes('save your current report')) {
                      <div class="system-actions">
                        <button mat-stroked-button color="primary" (click)="saveReport(); applyAiMessageGridState(aiMessages[$index - 1])">
                          Save &amp; Apply New
                        </button>
                        <button mat-stroked-button (click)="applyAiMessageGridState(aiMessages[$index - 1])">
                          Discard &amp; Apply New
                        </button>
                      </div>
                    }
                  </div>
                </div>
              }
            }

            <!-- Loading indicator -->
            @if (aiLoading()) {
              <div class="ai-message assistant-message">
                <mat-icon class="ai-avatar thinking">auto_awesome</mat-icon>
                <div class="message-bubble assistant-bubble thinking-bubble">
                  <div class="thinking-dots">
                    <span></span><span></span><span></span>
                  </div>
                  Analyzing your request...
                </div>
              </div>
            }
          </div>

          <!-- Input Area -->
          <div class="ai-input-area">
            @if (aiMessages.length > 0) {
              <button mat-button class="new-chat-btn" (click)="startNewAiConversation()" matTooltip="Start a new conversation">
                <mat-icon>refresh</mat-icon> New Chat
              </button>
            }
            <mat-form-field appearance="outline" class="ai-input-field">
              <mat-label>Describe the report you want...</mat-label>
              <input matInput
                     [(ngModel)]="aiPromptText"
                     (keyup.enter)="onAiSubmit()"
                     [disabled]="aiLoading()"
                     placeholder="e.g., Show me all contracts expiring this quarter..." />
              <button matSuffix mat-icon-button (click)="onAiSubmit()" [disabled]="aiLoading()" matTooltip="Submit">
                <mat-icon>send</mat-icon>
              </button>
            </mat-form-field>
          </div>
        </div>
      </mat-tab>

      <!-- Tab 2: Manual Builder (AG Grid) -->
      <mat-tab>
        <ng-template mat-tab-label>
          <mat-icon>table_chart</mat-icon>&nbsp;Manual Builder
        </ng-template>

        <div class="grid-container">
          <!-- Active Filters Banner -->
          @if (activeFilterCount() > 0) {
            <div class="filters-active-banner">
              <div class="filters-banner-left">
                <mat-icon class="filters-banner-icon">filter_list</mat-icon>
                <span class="filters-banner-text">
                  {{ activeFilterCount() }} {{ activeFilterCount() === 1 ? 'Filter' : 'Filters' }} Active
                </span>
                <span class="filters-banner-subtext">
                  Results are being filtered
                </span>
              </div>
              <button mat-raised-button color="warn" class="clear-filters-btn" (click)="clearAllFilters()">
                <mat-icon>clear_all</mat-icon>
                Clear All Filters
              </button>
            </div>
          }

          @if (isLoading()) {
            <div class="grid-loading-overlay">
              <mat-spinner diameter="60"></mat-spinner>
              <p>Loading data...</p>
            </div>
          }

          <ag-grid-angular
            [style.visibility]="isLoading() ? 'hidden' : 'visible'"
            style="width: 100%; height: 700px"
            class="ag-theme-balham"
            [defaultColDef]="defaultColDef"
            [rowData]="rowData"
            [columnDefs]="columnDefs"
            [sideBar]="sideBar"
            [animateRows]="true"
            [groupDisplayType]="'groupRows'"
            rowGroupPanelShow="always"
            [pagination]="true"
            [paginationPageSize]="100"
            [paginationPageSizeSelector]="[25, 50, 100, 500]"
            (gridReady)="onGridReady($event)"
            (filterChanged)="onFilterChanged()"
            (sortChanged)="onGridStateChanged()"
            (columnVisible)="onGridStateChanged()"
            (columnMoved)="onGridStateChanged()"
            (columnResized)="onGridStateChanged()"
          >
          </ag-grid-angular>
        </div>
      </mat-tab>

    </mat-tab-group>

  </mat-sidenav-content>
</mat-sidenav-container>
